import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useApi } from '@/shared/api/useApi';
import type { AutomationControlView, AutomationMode } from '@/shared/api/types/automationControl';

type Action =
  | { action: 'takeover' | 'resume' }
  | { action: 'assign'; ownerInstallationId: string | null; mode: AutomationMode };
export function useAutomationControl(id: string) {
  const api = useApi();
  const cache = useQueryClient();
  const inFlight = useRef(false);
  const key = ['inbox', 'automation', id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.automationControl.get(id),
    retry: false,
    refetchInterval: 15000,
  });
  const mutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: ({ action, state }: { action: Action; state: AutomationControlView }) => {
      const request = {
        clientRequestId: crypto.randomUUID(),
        expectedControlRevision: state.control.controlRevision,
        expectedConversationRevision: state.control.conversationRevision,
      };
      return action.action === 'assign'
        ? api.automationControl.set(id, {
            ...request,
            ownerInstallationId: action.ownerInstallationId,
            mode: action.mode,
          })
        : api.automationControl.operate(id, action.action, request);
    },
    onSuccess: (state) => {
      cache.setQueryData(key, state);
    },
    onSettled: async () => {
      await cache.invalidateQueries({ queryKey: key });
      inFlight.current = false;
    },
  });
  function run(action: Action) {
    if (inFlight.current || !query.data || query.isError) return;
    inFlight.current = true;
    mutation.mutate({ action, state: query.data });
  }
  return { query, mutation, run };
}
